import { refreshToken, jwtDecode } from '@/helper'
import store from '@/store.js'

// Token refresh interval in milliseconds (default: 5 minutes)
const DEFAULT_REFRESH_INTERVAL = 5 * 60 * 1000

// Minimum time before token expiration to trigger refresh (in seconds)
const MIN_TIME_BEFORE_EXPIRY = 60

let refreshInterval = null

/**
 * Calculates time until token expiration
 * @param {string} token - JWT token
 * @returns {number} - Time until expiration in seconds, or -1 if invalid
 */
function getTokenTimeToExpiry(token) {
  try {
    const decoded = jwtDecode(token)
    if (decoded && decoded.exp) {
      const currentTime = Math.floor(Date.now() / 1000)
      return decoded.exp - currentTime
    }
  } catch (e) {
    console.warn('Failed to decode token for expiry check:', e)
  }
  return -1
}

/**
 * Determines if token should be refreshed
 * @param {string} token - JWT token
 * @returns {boolean} - True if token should be refreshed
 */
function shouldRefreshToken(token) {
  if (!token) return false
  const timeToExpiry = getTokenTimeToExpiry(token)
  // Refresh if token expires in less than 10 minutes (600 seconds)
  return timeToExpiry > 0 && timeToExpiry < 600
}

/**
 * Performs token refresh
 * @returns {Promise<boolean>} - True if refresh was successful
 */
async function performTokenRefresh() {
  try {
    const currentToken = localStorage.getItem('pass')

    if (!currentToken || currentToken === '' || currentToken === 'null') {
      return false
    }

    // Check if token needs refresh
    if (!shouldRefreshToken(currentToken)) {
      return true // Token is still valid, no refresh needed
    }

    const res = await refreshToken()

    if (res && res.result === 'OK' && res.token) {
      // Update localStorage with new token
      localStorage.setItem('pass', res.token)

      // Update Vuex store with new token
      const decoded = jwtDecode(res.token)
      store.dispatch('setUser', { token: res.token, content: decoded })

      return true
    }

    return false
  } catch (e) {
    console.warn('Token refresh failed:', e)
    return false
  }
}

/**
 * Starts the periodic token refresh service
 * @param {number} intervalMs - Refresh check interval in milliseconds
 */
function startTokenRefresh(intervalMs = DEFAULT_REFRESH_INTERVAL) {
  // Don't start if external auth is used (msal)
  if (process.env.VUE_APP_EXTERNAL_AUTH && process.env.VUE_APP_EXTERNAL_AUTH === "msal") {
    return
  }

  // Clear any existing interval
  stopTokenRefresh()

  // Perform initial check
  performTokenRefresh()

  // Set up periodic refresh
  refreshInterval = setInterval(async () => {
    const success = await performTokenRefresh()
    if (!success) {
      // If refresh failed and token is expired, stop the service
      const token = localStorage.getItem('pass')
      if (token && getTokenTimeToExpiry(token) <= MIN_TIME_BEFORE_EXPIRY) {
        console.warn('Token expired and refresh failed, stopping refresh service')
        stopTokenRefresh()
      }
    }
  }, intervalMs)
}

/**
 * Stops the periodic token refresh service
 */
function stopTokenRefresh() {
  if (refreshInterval) {
    clearInterval(refreshInterval)
    refreshInterval = null
  }
}

/**
 * Checks if token refresh service is running
 * @returns {boolean}
 */
function isTokenRefreshRunning() {
  return refreshInterval !== null
}

export {
  startTokenRefresh,
  stopTokenRefresh,
  performTokenRefresh,
  shouldRefreshToken,
  getTokenTimeToExpiry,
  isTokenRefreshRunning,
  DEFAULT_REFRESH_INTERVAL
}
